/**
 * The player skeleton — 14 joints in the simulation's Z-up frame.
 *
 * This is the original's `player.object` node tree (`ObjectLoader::LoadObject`,
 * objectloader.cpp:72) reduced to what it is actually used for: a parent-linked list of
 * joints, each with a local position that never changes and a local rotation that an
 * animation frame overwrites every tick.
 *
 * WHAT DRIVES IT. `Animation::Apply` (animation.cpp:370-720) writes ONE local rotation per
 * joint, and a position onto the root only (`animation.cpp:708-714`). Nothing else moves;
 * bone lengths are fixed by the file. The facing direction is folded into the "body" joint's
 * rotation rather than the root's (`animation.cpp:417-422`), which is why `setFacing` here
 * multiplies into that joint and not into the root.
 *
 * WORLD TRANSFORMS. Joint 0 ("player") is absolute; every other joint composes with its
 * parent in the ordinary way (`Spatial::GetDerivedPosition/Rotation`, types/spatial.cpp:106-143):
 *
 *     worldRot(j)   = worldRot(parent) * localRot(j)
 *     worldPos(j)   = worldPos(parent) + worldRot(parent) * localPos(j)
 *
 * Joints are stored in the order the original's `Node::GetNodes(recurse)` yields them —
 * depth-first, pre-order — because the body mesh's bone indices are positions in that list.
 * The one wrinkle is that `GetNodes` excludes the node it is called on
 * (`scene/scene3d/node.cpp:88-96`), so the original's bone index N is this array's N+1. The
 * conversion already applied that shift; see tools/convert/skinning.py.
 */

import { Quaternion, Vector3 } from 'three';

export interface SkeletonJoint {
  index: number;
  name: string;
  parent: number | null;
  position: [number, number, number];
  rotation: [number, number, number, number];
  geometry?: { filename: string | null; name: string | null } | null;
}

/** The shape of public/assets/skeleton/player.skeleton.json. */
export interface SkeletonData {
  rootName: string;
  jointCount: number;
  jointNames: string[];
  joints: SkeletonJoint[];
}

/**
 * One reference pose or animation frame: a local rotation per joint, plus the root's position.
 * The shape of public/assets/skeleton/restpose.*.json.
 */
export interface PoseData {
  source: string;
  frameCount: number;
  root: { name: string; keys: { frame: number; position: [number, number, number] }[] };
  joints: { name: string; keys: { frame: number; rotation: [number, number, number, number] }[] }[];
}

export class Skeleton {
  readonly names: string[];
  readonly geometryNames: (string | null)[];
  readonly geometryFiles: (string | null)[];
  readonly parents: (number | null)[];
  /** Local offset from the parent joint. Fixed by player.object; never animated. */
  readonly localPosition: Vector3[];
  readonly localRotation: Quaternion[];
  /** Recomputed by update(). Indexed the same way as the joints. */
  readonly worldPosition: Vector3[];
  readonly worldRotation: Quaternion[];

  private readonly scratchRotation = new Quaternion();
  private readonly scratchOffset = new Vector3();

  constructor(data: SkeletonData) {
    this.names = data.joints.map((j) => j.name);
    this.geometryNames = data.joints.map((j) => j.geometry?.name ?? null);
    this.geometryFiles = data.joints.map((j) => j.geometry?.filename ?? null);
    this.parents = data.joints.map((j) => j.parent);
    this.localPosition = data.joints.map((j) => new Vector3(...j.position));
    this.localRotation = data.joints.map((j) => new Quaternion(...j.rotation));
    this.worldPosition = data.joints.map(() => new Vector3());
    this.worldRotation = data.joints.map(() => new Quaternion());

    // The joint list must be topologically ordered — every parent before its children —
    // or update() would read a stale parent. It is, because the converter wrote it in the
    // same depth-first order the original walks, but a silent violation here would show up
    // as a subtly broken pose rather than an error, so it is checked.
    data.joints.forEach((joint, i) => {
      if (joint.parent !== null && joint.parent >= i) {
        throw new Error(
          `Skeleton joint "${joint.name}" (${i}) lists parent ${joint.parent}, which comes later`,
        );
      }
    });

    this.update();
  }

  indexOf(name: string): number {
    const index = this.names.indexOf(name);
    if (index < 0) throw new Error(`No joint "${name}" in the player skeleton`);
    return index;
  }

  /** Overwrite every joint's local rotation from a pose, and the root's position. */
  setPose(pose: PoseData, frame = 0): void {
    const rootIndex = this.names.indexOf(pose.root.name);
    if (rootIndex >= 0) {
      const key = keyAt(pose.root.keys, frame);
      if (key) this.localPosition[rootIndex]!.set(...key.position);
    }

    for (const joint of pose.joints) {
      const index = this.names.indexOf(joint.name);
      if (index < 0) continue;
      const key = keyAt(joint.keys, frame);
      if (key) this.localRotation[index]!.set(...key.rotation);
    }
  }

  /**
   * Turn the player to face a compass direction, in radians about the up axis.
   *
   * Applied to the "body" joint, pre-multiplied, exactly as `Animation::Apply` does
   * (animation.cpp:417-422). The root joint carries position only.
   */
  setFacing(radians: number): void {
    const body = this.indexOf('body');
    this.scratchRotation.setFromAxisAngle(UP, radians);
    this.localRotation[body]!.premultiply(this.scratchRotation).normalize();
  }

  setRootPosition(position: Vector3): void {
    this.localPosition[0]!.copy(position);
  }

  /** Recompute every joint's world transform. Cheap: 14 joints, no allocation. */
  update(): void {
    for (let i = 0; i < this.parents.length; i++) {
      const parent = this.parents[i]!;
      if (parent === null) {
        this.worldPosition[i]!.copy(this.localPosition[i]!);
        this.worldRotation[i]!.copy(this.localRotation[i]!);
      } else {
        this.worldRotation[i]!
          .copy(this.worldRotation[parent]!)
          .multiply(this.localRotation[i]!)
          .normalize();
        this.scratchOffset
          .copy(this.localPosition[i]!)
          .applyQuaternion(this.worldRotation[parent]!);
        this.worldPosition[i]!.copy(this.worldPosition[parent]!).add(this.scratchOffset);
      }
    }
  }
}

const UP = new Vector3(0, 0, 1);

/**
 * The pose value at a frame.
 *
 * The original interpolates between `frame` and `frame + 1` and, unless a caller passes a
 * sub-frame time, blends them half and half (`animation.cpp:391-405`). For the three
 * reference poses that is moot — they hold one value from first frame to last. This takes
 * the last key at or before the requested frame, which agrees with the original on constant
 * poses and does not pretend to be the full interpolator; playback belongs with the
 * animation system, not here.
 */
function keyAt<T extends { frame: number }>(keys: T[], frame: number): T | undefined {
  let found: T | undefined = keys[0];
  for (const key of keys) {
    if (key.frame <= frame) found = key;
    else break;
  }
  return found;
}
